<template>
  <v-alert
    v-if="teachers.length > 0"
    rounded
    outlined
    light
    color="deep-purple"
    class="pa-1 py-3"
  >
    <v-card-title class="ma-0 py-2 pl-1">
      <h6 class="deep-purple--text lighten-2 font-weight-semibold">
        Docentes del curso
      </h6>
    </v-card-title>

    <v-card-text class="pb-0">
      <div v-if="loading" class="d-flex justify-center py-2">
        <v-progress-circular indeterminate color="deep-purple" size="24" />
      </div>

      <div v-else>
        <div
          v-for="teacher in teachers"
          :key="teacher.id"
          class="d-flex align-center justify-space-between py-1"
        >
          <span class="black--text font-weight-light">
            {{ teacher.firstname }} {{ teacher.lastname }}
          </span>
          <v-btn
            icon
            small
            :title="$t('students.impersonate')"
            @click="openImpersonateModal(teacher)"
          >
            <v-icon size="20" color="deep-purple">mdi-account-switch</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card-text>

    <general-modal-container
      v-if="isImpersonateModalOpen"
      @onClose="closeImpersonateModal"
    >
      <impersonate-app-selector-modal
        :user-name="impersonateUserLabel"
        @onSelect="handleImpersonateAppSelect"
        @onCancel="closeImpersonateModal"
      />
    </general-modal-container>
  </v-alert>
</template>

<script>
import userService from '@/services/user.service';
import impersonationService from '@/services/impersonation.service';
import GeneralModalContainer from '@/components/core/modals/GeneralModalContainer.vue';
import ImpersonateAppSelectorModal from '@/components/teachers/modals/ImpersonateAppSelectorModal.vue';
import LoadingMixin from '@/mixins/loading';
import SnackbarMixin from '@/mixins/snackbar';
import {
  buildImpersonationPayload,
  buildImpersonationLabel,
  getImpersonationErrorMessage,
} from '@/utils/impersonation';
import { hasActiveCourseMatching } from '@/utils/teacherCourses';

export default {
  name: 'StudentCourseTeachers',
  components: {
    GeneralModalContainer,
    ImpersonateAppSelectorModal,
  },
  mixins: [LoadingMixin, SnackbarMixin],
  props: {
    schoolId: {
      type: [Number, String],
      required: true,
    },
    gradeId: {
      type: [Number, String],
      default: null,
    },
    section: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    loading: false,
    teachers: [],
    userToImpersonate: null,
    isImpersonateModalOpen: false,
  }),
  computed: {
    impersonateUserLabel() {
      return this.userToImpersonate
        ? buildImpersonationLabel(this.userToImpersonate)
        : '';
    },
  },
  async mounted() {
    this.loading = true;
    try {
      const { data, status } = await userService.findAllTeachersBySchool(this.schoolId, 1, true);
      if (status === 200) {
        const allTeachers = Array.isArray(data) ? data : [];
        // Docentes asignados al curso del alumno (grado + sección). KB-971: solo cuentan
        // las asignaciones a cursos activos, las únicas que ve la app de docentes.
        this.teachers = allTeachers.filter((teacher) => hasActiveCourseMatching(
          teacher.courseteachers,
          this.gradeId,
          this.section,
        ));
      }
    } catch (e) {
      console.error('[BEE-451] Error al obtener docentes del curso:', e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    openImpersonateModal(teacher) {
      const payload = buildImpersonationPayload(teacher);
      if (!payload) {
        this.snackbarError(this.$t('students.action_error'));
        return;
      }
      this.userToImpersonate = teacher;
      this.isImpersonateModalOpen = true;
    },
    closeImpersonateModal() {
      this.isImpersonateModalOpen = false;
      this.userToImpersonate = null;
    },
    async handleImpersonateAppSelect(app) {
      const teacher = this.userToImpersonate;
      this.closeImpersonateModal();
      await this.impersonateUser(teacher, app === 'teachers');
    },
    async impersonateUser(teacher, useTeachersApp) {
      const payload = buildImpersonationPayload(teacher);
      if (!payload) {
        this.snackbarError(this.$t('students.action_error'));
        return;
      }
      this.loader();
      try {
        if (useTeachersApp) {
          const response = await impersonationService.impersonate(payload);
          const redirectURL = response?.content?.redirectURL;
          if (!redirectURL) {
            throw new Error(String(response?.content ?? this.$t('students.action_error')));
          }
          window.open(redirectURL, '_blank');
        } else {
          const response = await impersonationService.impersonateForDataSystem(payload);
          if (!response?.redirectURL) {
            throw new Error(this.$t('students.action_error'));
          }
          window.open(response.redirectURL, '_blank');
        }
        this.snackbarSuccess(
          `${this.$t('students.impersonate_opening')} ${buildImpersonationLabel(teacher)}`.trim(),
        );
      } catch (error) {
        this.snackbarError(
          getImpersonationErrorMessage(error, this.$t('students.action_error')),
        );
      } finally {
        this.loader();
      }
    },
  },
};
</script>
